import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { UserApiService } from './user-api.service';
import * as userActions from './user.actions';
import * as userSelectors from './user.selectors';
import * as fromAuth from '../../../authentication/store/auth';
import { TranslateService } from '@ngx-translate/core';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private api: UserApiService,
    private store$: Store<any>,
    private translate: TranslateService,
    private notify: SystemNotificationsService,
    private spinnerService: NgxSpinnerService
  ) {}

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.getUsers),
      withLatestFrom(this.store$.select(userSelectors.selectQueryParams), this.store$.select(fromAuth.selectUserContext)),
      switchMap(([action, params, userContext]) => {
        return this.api.searchUsers(userContext.organizationUId, { ...params, ...action.params }).pipe(
          map(results => {
            return userActions.loadUsers({ results });
          }),
          catchError(err => of(userActions.setError({ err })))
        );
      })
    )
  );

  inviteUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.inviteUsers),
      tap(() => this.store$.dispatch(userActions.setIsInviting({ isInviting: true }))),
      withLatestFrom(
        this.store$.select(fromAuth.selectUserContext),
        this.store$.select(userSelectors.selectQueryParams),
        this.store$.select(userSelectors.selectSearchTerm)
      ),
      switchMap(([action, userContext, queryParams, searchTerm]) =>
        this.api.inviteUsers(userContext.organizationUId, action.users).pipe(
          switchMap(res => {
            if (res) {
              res.forEach(fail => {
                this.translate.get(`ERRORS.${fail.message}`).subscribe(value => {
                  this.notify.error(`Unable to add ${fail.email}. Error message: ${value}`);
                });
              });
            }
            return [
              userActions.getUsers({ params: { ...queryParams, searchTerm } }),
              userActions.setIsInviting({ isInviting: false }),
              userActions.showInviteForm({ showInviteForm: false })
            ];
          }),
          catchError(e => {
            return of(
              userActions.setError({ err: e }),
              userActions.setIsInviting({ isInviting: false }),
              userActions.showInviteForm({ showInviteForm: false })
            );
          })
        )
      )
    )
  );

  reinviteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.reinviteUser),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([action, userContext]) => {
        return this.api.reinviteUser(userContext.organizationUId, action.user).pipe(
          switchMap(() => []),
          catchError(err => of(userActions.setError({ err })))
        );
      })
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.deleteUser),
      withLatestFrom(
        this.store$.select(userSelectors.selectQueryParams),
        this.store$.select(userSelectors.selectSearchTerm),
        this.store$.select(fromAuth.selectUserContext),
        this.store$.select(userSelectors.selectSelectedUIds)
      ),
      switchMap(([{ userUId }, queryParams, searchTerm, userContext, selectedUIds]) => {
        return this.api.deleteUser(userContext.organizationUId, userUId).pipe(
          switchMap(res => this.api.searchUsers(userContext.organizationUId, { ...queryParams, searchTerm })),
          switchMap(results => {
            this.notify.success(this.translate.instant('MANAGEMENT.USERS.SUCCESS-DELETE', { count: 1 }));

            const actions: Action[] = [];

            if (selectedUIds.has(userUId)) {
              selectedUIds.delete(userUId);
              actions.push(userActions.setSelectedUIds({ selectedUIds: new Set(selectedUIds) }));
            }

            if (results.skip >= results.totalCount) {
              const params = {
                ...queryParams,
                skip: queryParams.skip - queryParams.take,
                include: 'AuthConnection,SecurityGroupMemberDetails',
                where: 'IsServiceAccount==false',
                searchTerm
              };
              actions.push(userActions.getUsers({ params }));
            } else {
              actions.push(userActions.loadUsers({ results }));
            }

            return actions;
          }),
          catchError(err => {
            console.error(err);
            this.notify.error(this.translate.instant('MANAGEMENT.USERS.ERROR-DELETE-SINGLE'));
            return of(userActions.setError({ err }));
          })
        );
      })
    )
  );

  deleteSelectedUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.deleteSelectedUsers),
      withLatestFrom(
        this.store$.select(userSelectors.selectQueryParams),
        this.store$.select(userSelectors.selectSearchTerm),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([action, queryParams, searchTerm, userContext]) => {
        this.spinnerService.show('users-list');

        let deletedCount = 0;

        return this.api.deleteSelectedUsers(userContext.organizationUId, action.userUIds).pipe(
          switchMap(usersDeletedCount => {
            deletedCount = usersDeletedCount;
            return this.api.searchUsers(userContext.organizationUId, { ...queryParams, searchTerm });
          }),
          switchMap(results => {
            this.spinnerService.hide('users-list');

            if (deletedCount > 0) {
              this.notify.success(this.translate.instant('MANAGEMENT.USERS.SUCCESS-DELETE', { count: deletedCount }));
            } else {
              this.notify.error(this.translate.instant('MANAGEMENT.USERS.ERROR-DELETE-MULTIPLE'));
            }

            const actions: Action[] = [userActions.clearSelectedUIds()];

            if (results.skip >= results.totalCount) {
              const params = {
                ...queryParams,
                skip: queryParams.skip - queryParams.take,
                include: 'AuthConnection,SecurityGroupMemberDetails',
                where: 'IsServiceAccount==false',
                searchTerm
              };
              actions.push(userActions.getUsers({ params }));
            } else {
              actions.push(userActions.loadUsers({ results }));
            }

            return actions;
          }),
          catchError(err => {
            console.error(err);
            this.spinnerService.hide('users-list');
            this.notify.error(this.translate.instant('MANAGEMENT.USERS.ERROR-DELETE-MULTIPLE'));
            return of(userActions.setError({ err }));
          })
        );
      })
    )
  );

  saveUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.saveUser),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([{ user }, userContext]) =>
        this.api.updateUser(userContext.organizationUId, user).pipe(
          switchMap(res => [userActions.updateUser({ user: res }), userActions.showEditForm({ showEditForm: false })]),
          catchError(err => of(userActions.setError({ err })))
        )
      )
    )
  );

  updateTotalUserCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.updateTotalUserCount, userActions.loadUsers),
      withLatestFrom(
        this.store$.select(userSelectors.selectSearchTerm),
        this.store$.select(userSelectors.selectQueryParams),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([, searchTerm, queryParams, userContext]) => {
        if (!searchTerm) {
          return [userActions.setTotalUserCount({ totalUserCount: queryParams.totalCount })];
        }

        return this.api.getTotalUserCount(userContext.organizationUId).pipe(
          switchMap(totalUserCount => [userActions.setTotalUserCount({ totalUserCount })]),
          catchError(err => {
            console.error(err);
            return of(userActions.setError({ err }));
          })
        );
      })
    )
  );
}
